'use client'

import { createContext, type ReactNode, useMemo } from 'react'
import { type StoreAnalytics, type StorePrivacy } from 'ecosystem'
import { type DataLayerManager } from '../managers/data-layer-manager'
import { type ConsentManager } from '../managers/consent-manager'
import { useGTMSetup } from '../hooks/useGtmSetup'
import { resolveDataCollectionConfig } from '../config'
import { type ConsentState, type DataCollectionConfig } from '../typings'

interface GTMContextType {
  dataLayerManager: DataLayerManager | null
  consentManager: ConsentManager | null
  isInitialized: boolean
  error: Error | null
  config: DataCollectionConfig | null
}

export const GTMContext = createContext<GTMContextType>({
  dataLayerManager: null,
  consentManager: null,
  isInitialized: false,
  error: null,
  config: null
})

export function GTMProvider({
  children,
  strictMode = true,
  storeId,
  isDebugMode,
  analytics,
  privacy,
  defaultConsentState
}: {
  children: ReactNode
  strictMode?: boolean
  storeId: string
  isDebugMode?: boolean
  analytics?: StoreAnalytics
  privacy?: StorePrivacy
  defaultConsentState?: Partial<Omit<ConsentState, 'client_timestamp'>>
}) {
  const hasValidConfig = Boolean(analytics?.gtmId)
  const configErrorText = 'GTMProvider: Incomplete Analytics configuration'

  if (!hasValidConfig && strictMode) {
    throw new Error(configErrorText)
  }

  const gtmConfig: DataCollectionConfig = useMemo(
    () =>
      resolveDataCollectionConfig({
        storeId,
        isDebugMode,
        analytics,
        privacy,
        defaultConsentState
      }),
    [storeId, isDebugMode, privacy, analytics, defaultConsentState]
  )

  const { dataLayerManager, consentManager, isInitialized, error, config } = useGTMSetup(
    gtmConfig,
    strictMode
  )

  if (!hasValidConfig || !isInitialized) {
    if (isDebugMode) {
      if (!hasValidConfig) {
        console.error(configErrorText)
      } else {
        console.log('GTMProvider: Not initialized')
      }
    }
    return <>{children}</>
  }

  if (isDebugMode) {
    console.log('GTMProvider: Initialized')
  }

  return (
    <GTMContext.Provider value={{ dataLayerManager, consentManager, isInitialized, error, config }}>
      {children}
    </GTMContext.Provider>
  )
}
